import { useState, useEffect, useCallback, useRef } from 'react';
import {
  type DispositivoVideoCaptura,
  type HdmiEstadoBroadcast,
  enumerarDispositivosVideo,
  lerHdmiDeviceIdSalvo,
  resolverDeviceIdInicial,
  salvarHdmiDeviceId,
} from '@/shared/lib/hdmiDevices';

export type { HdmiEstadoBroadcast } from '@/shared/lib/hdmiDevices';

type SendToTela = (event: string, payload: Record<string, unknown>) => void;

export function useHdmiCaptura(sendToTela: SendToTela) {
  const [dispositivos, setDispositivos] = useState<DispositivoVideoCaptura[]>([]);
  const [deviceIdAtivo, setDeviceIdAtivo] = useState(() => lerHdmiDeviceIdSalvo());
  const dispositivosRef = useRef(dispositivos);
  const deviceIdRef     = useRef(deviceIdAtivo);

  useEffect(() => { dispositivosRef.current = dispositivos; }, [dispositivos]);
  useEffect(() => { deviceIdRef.current = deviceIdAtivo; }, [deviceIdAtivo]);

  const broadcastEstado = useCallback((disp: DispositivoVideoCaptura[], ativo: string) => {
    const payload: HdmiEstadoBroadcast = { dispositivos: disp, deviceIdAtivo: ativo };
    sendToTela('hdmi_estado', payload as unknown as Record<string, unknown>);
  }, [sendToTela]);

  const refreshDispositivos = useCallback(async (): Promise<HdmiEstadoBroadcast> => {
    const videos = await enumerarDispositivosVideo();
    setDispositivos(videos);

    const salvo = lerHdmiDeviceIdSalvo();
    const { deviceId } = resolverDeviceIdInicial(videos, salvo);
    const ativo = (salvo && videos.some(d => d.deviceId === salvo))
      ? salvo
      : (deviceId || deviceIdRef.current);

    if (ativo && ativo !== salvo) {
      salvarHdmiDeviceId(ativo);
    }
    if (ativo) setDeviceIdAtivo(ativo);

    broadcastEstado(videos, ativo);
    return { dispositivos: videos, deviceIdAtivo: ativo };
  }, [broadcastEstado]);

  const selecionarDispositivo = useCallback((id: string) => {
    // '' = placa sumiu (NotFoundError no CaptureSlide): limpa o id morto. Antes
    // era ignorado — o efeito tentava o mesmo id de novo e o seletor reabria em
    // loop ao Cancelar; o Controle Remoto seguia vendo o id morto como ativo.
    if (!id) {
      setDeviceIdAtivo('');
      deviceIdRef.current = '';
      broadcastEstado(dispositivosRef.current, '');
      return;
    }
    salvarHdmiDeviceId(id);
    setDeviceIdAtivo(id);
    broadcastEstado(dispositivosRef.current, id);
  }, [broadcastEstado]);

  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md) return;
    const onDeviceChange = () => { void refreshDispositivos(); };
    md.addEventListener('devicechange', onDeviceChange);
    return () => md.removeEventListener('devicechange', onDeviceChange);
  }, [refreshDispositivos]);

  return {
    dispositivos,
    deviceIdAtivo,
    refreshDispositivos,
    selecionarDispositivo,
    broadcastEstadoAtual: () => broadcastEstado(dispositivosRef.current, deviceIdRef.current),
  };
}
